/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Constants,
  Time,
  Seconds,
  TimeUtils,
  TvAsset,
  Vod,
  TvAssetUtils,
  RelativeTime,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';

export enum SpriteType {
  CATCHUP = 'CATCHUP',
  LIVE = 'LIVE',
  NPVR = 'NPVR',
  VOD = 'VOD',
}

export enum QueryParameters {
  END_TIME = 'end_time',
  PAGE = 'page',
  RESOLUTION = 'resolution',
  SPRITE = 'sprite',
}

export default class VisualScrubberUtils {
  private static SECONDS_PER_THUMBNAIL = Constants.SECONDS_PER_SPRITE / Constants.NUMBER_THUMBNAILS_PER_SPRITE;

  private static LAST_THUMBNAIL_INDEX = 9;

  private static THUMB_STOP_WIDTH_PERCENTAGE = ((Constants.ACTIVE_RESOLUTION.width / 2) * 100) / (12 * 120);

  private static getEndTimeForLiveAsset(startDate: Time, progress: Seconds): string {
    const startDateUnixEpoch = TimeUtils.toUnixEpoch(startDate);
    return encodeURIComponent(
      TimeUtils.toIsoStringSeconds(
        TimeUtils.fromUnixEpoch(
          Math.floor((startDateUnixEpoch + progress) / this.SECONDS_PER_THUMBNAIL) * this.SECONDS_PER_THUMBNAIL
        )
      )
    );
  }

  static getUrlParameters(asset: TvAsset | Vod, progress: Seconds, spriteIndex?: number): string | undefined {
    const { resolution } = Constants.ACTIVE_RESOLUTION;
    const page = spriteIndex || this.getPage(progress);

    if (AssetUtils.isEpg(asset)) {
      if (TvAssetUtils.getRelativeTime(asset) === RelativeTime.LIVE) {
        return `&${QueryParameters.SPRITE}=${SpriteType.LIVE}&${QueryParameters.END_TIME}=${this.getEndTimeForLiveAsset(
          asset.startDate,
          progress
        )}&${QueryParameters.RESOLUTION}=${resolution}`;
      }
      return `&${QueryParameters.SPRITE}=${SpriteType.CATCHUP}&${QueryParameters.PAGE}=${page}&${QueryParameters.RESOLUTION}=${resolution}`;
    }

    if (AssetUtils.isVod(asset)) {
      return `&${QueryParameters.SPRITE}=${SpriteType.VOD}&${QueryParameters.PAGE}=${page}&${QueryParameters.RESOLUTION}=${resolution}`;
    }

    if (AssetUtils.isRecording(asset)) {
      return `&${QueryParameters.SPRITE}=${SpriteType.NPVR}&${QueryParameters.PAGE}=${page}&${QueryParameters.RESOLUTION}=${resolution}`;
    }

    return undefined;
  }

  static removeUrlParameters(url: string): string {
    const splitedUrl = url.split('&');
    const filteredUrl = splitedUrl.filter(
      (e) =>
        !e.startsWith(QueryParameters.SPRITE) &&
        !e.startsWith(QueryParameters.RESOLUTION) &&
        !e.startsWith(QueryParameters.END_TIME) &&
        !e.startsWith(QueryParameters.PAGE)
    );
    return filteredUrl.join('&');
  }

  private static getPage(progress: Seconds): number {
    return Math.floor(progress / this.SECONDS_PER_THUMBNAIL / Constants.NUMBER_THUMBNAILS_PER_SPRITE);
  }

  static getThumbnailIndex(asset: TvAsset | Vod, progress: Seconds): number {
    if (AssetUtils.isEpg(asset) && TvAssetUtils.getRelativeTime(asset) === RelativeTime.LIVE) {
      return this.LAST_THUMBNAIL_INDEX;
    }

    return Math.floor((progress % Constants.SECONDS_PER_SPRITE) / this.SECONDS_PER_THUMBNAIL);
  }

  static getComponentOffset(duration: Seconds, paddingStart: Seconds, paddingEnd: Seconds, progress: Seconds): number {
    let scrubberOffset = (progress / (duration - paddingStart - paddingEnd)) * 100;

    if (scrubberOffset <= this.THUMB_STOP_WIDTH_PERCENTAGE) {
      scrubberOffset = 0;
    } else if (
      scrubberOffset > this.THUMB_STOP_WIDTH_PERCENTAGE &&
      scrubberOffset < 100 - this.THUMB_STOP_WIDTH_PERCENTAGE
    ) {
      scrubberOffset -= this.THUMB_STOP_WIDTH_PERCENTAGE;
    } else {
      scrubberOffset = 100 - this.THUMB_STOP_WIDTH_PERCENTAGE * 2;
    }

    return scrubberOffset;
  }
}
